import { useState } from 'react';
import { useAuth } from '../auth/AuthProvider';

const REJECT_COPY: Record<string, string> = {
  no_match: "This account isn't linked to a Captomi customer. Contact us to get set up.",
  inactive: 'This account is inactive. Contact Captomi support.',
  no_identifier: 'We could not read a verified email for this sign-in. Try another method.',
  session_failed: 'Something went wrong signing you in. Please try again.',
};

export function Login({ rejected }: { rejected?: string }) {
  const { sign_in_google, sign_in_email, send_sign_in_link, send_password_reset } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setErr(null);
    setNotice(null);
    try {
      await fn();
    } catch {
      setErr('Sign-in failed. Check your details and try again.');
    } finally {
      setBusy(false);
    }
  }

  async function email_a_link() {
    if (!email) {
      setErr('Enter your email first.');
      return;
    }
    await run(async () => {
      await send_sign_in_link(email);
      setNotice(`Check ${email} for a sign-in link.`);
    });
  }

  // Standard forgot-password: doubles as first-time set (the emailed link sets OR resets the
  // password). Generic confirmation — never reveal whether the account exists (enumeration-safe).
  async function reset_password() {
    if (!email) {
      setErr('Enter your email first.');
      return;
    }
    await run(async () => {
      await send_password_reset(email);
      setNotice(`If an account exists for ${email}, we’ve emailed a link to set or reset your password.`);
    });
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 16,
        background: 'radial-gradient(1100px 520px at 50% -10%, var(--accent-weak), var(--bg) 60%)',
      }}
    >
      <div className="card" style={{ width: 380, maxWidth: '100%', padding: 28 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 22 }}>
          <span
            className="mark"
            style={{ width: 44, height: 44, borderRadius: 12, fontSize: 22, marginBottom: 12 }}
          >
            C
          </span>
          <h2 style={{ margin: 0 }}>Sign in to Captomi</h2>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13.5 }}>Your workspace and apps in one place.</p>
        </div>

        {rejected && (
          <p className="error" style={{ background: 'var(--danger-weak)', padding: '10px 12px', borderRadius: 8, fontSize: 13.5 }}>
            {REJECT_COPY[rejected] ?? REJECT_COPY.session_failed}
          </p>
        )}

        <button className="btn block lg" disabled={busy} onClick={() => run(sign_in_google)}>
          Continue with Google
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0' }}>
          <span className="divider" style={{ flex: 1 }} />
          <span className="subtle" style={{ fontSize: 12 }}>or</span>
          <span className="divider" style={{ flex: 1 }} />
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void run(() => sign_in_email(email, password));
          }}
        >
          <input
            type="email"
            placeholder="you@business.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ marginBottom: 8 }}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ marginBottom: 12 }}
          />
          <button className="btn block" disabled={busy} type="submit">
            Sign in
          </button>
        </form>

        <div style={{ textAlign: 'center', margin: '12px 0' }}>
          <button
            type="button"
            disabled={busy}
            onClick={() => void reset_password()}
            style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 13 }}
          >
            Forgot or set password?
          </button>
        </div>

        <button className="btn secondary block" disabled={busy} onClick={() => void email_a_link()}>
          Email me a sign-in link
        </button>

        {notice && <p className="muted" style={{ marginTop: 14, marginBottom: 0, fontSize: 13.5 }}>{notice}</p>}
        {err && <p className="error" style={{ marginTop: 14, marginBottom: 0, fontSize: 13.5 }}>{err}</p>}
      </div>
    </div>
  );
}
